import * as React from 'react';

import { warn } from 'helper/debug';
import i18n from 'helper/i18n';

import { CardTypes } from 'defs/card-types';
import { getMobileDevicePlatform, iOSScrollKeyboardPositionFix, PLATFORMS } from 'helper/device';

import Link from 'components/atoms/Link';
import withSequenceState from 'components/hoc/withSequenceState';
import ButtonBar from 'components/molecules/ButtonBar';
import EditableArea from 'components/molecules/EditableArea';
import Rich from 'components/molecules/Rich';
import Sequences from 'defs/sequences';

import { LinkProps } from 'components/atoms/Link';
import { WithHandleCompletePropsType } from 'components/hoc/Cards/withHandleComplete';
import { CardStateTypes } from 'components/molecules/Card';
import { ButtonProps } from '../../Button/Button';

export type CardDetailSloganOriginPropsType = {
	id: number;
	isDone: boolean;
	infoText?: string;
	link?: LinkProps;
	slogan: string;
	isCheckable: boolean;
	isUpdatingSlogan: boolean;
	uiState: CardStateTypes; // used to show the correct buttons
	handleCardContentClick: Function;
	handleShowDetailsClick: Function;
	handleCompleteCard: (cb?: Function) => void;
	handleCardSloganFocus: (textareaDomElement: HTMLTextAreaElement) => void;
	doSaveSlogan: (id: number, slogan: string) => void;
	isSequenceLoading: boolean;
};

export type CardDetailSloganPropsType = CardDetailSloganOriginPropsType & WithHandleCompletePropsType;

export type CardDetailSloganState = {
	slogan: string;
	optimisticSaveSlogan: string;
	isEditing: boolean;
};

export class CardDetailSlogan extends React.Component<CardDetailSloganPropsType, CardDetailSloganState> {
	public static displayName = 'CardDetailSlogan';

	public state = {
		slogan: this.props.slogan,
		optimisticSaveSlogan: this.props.slogan,
		isEditing: false,
	};

	get isIOS(): boolean {
		return getMobileDevicePlatform() === PLATFORMS.IOS;
	}

	public componentDidUpdate(prevProps: CardDetailSloganPropsType) {
		if (this.props.uiState === 'feed' && prevProps.uiState !== 'feed') {
			this.setState({
				isEditing: false,
			});
		}
	}

	public handleButtonClicks = (buttonType: string) => {
		if (this.props.isSequenceLoading) {
			return;
		}

		switch (buttonType) {
			case 'completed':
				if ((!this.props.isDone && !this.state.isEditing) || this.props.isDone) {
					this.setState({
						isEditing: false,
						optimisticSaveSlogan: this.state.slogan,
					});

					this.props.onComplete({
						type: CardTypes.Slogan as any,
						onAnimationDone: () => {
							this.props.doSaveSlogan(this.props.id, this.state.slogan);
						},
					});
				} else {
					this.setState({ isEditing: false, optimisticSaveSlogan: this.state.slogan });
				}
				break;
			case 'close':
				this.setState({
					isEditing: false,
					slogan: this.props.slogan,
				});
				this.props.handleCompleteCard();
				this.handleCloseCard();
				break;
			default:
				warn(`Button Action "${buttonType}" not available`);
		}
	};

	public overrideHandleCloseCard = (handleCloseCard: () => void) => {
		this.handleCloseCard = handleCloseCard;
	};

	public handleCloseCard = () => {
		/* noop */
	};

	public handleSloganChange = (newSlogan: string) => {
		this.setState({ slogan: newSlogan });
	};

	public handleEditStatusChange = () => {
		this.setState({ isEditing: !this.state.isEditing });
	};

	public getButtonText = () => {
		const { isDone } = this.props;
		if (isDone) {
			return i18n('react.impulsCoach.components.cardDetail.slogan.linkText.edit');
		}

		return i18n('react.impulsCoach.components.cardDetail.slogan.linkText.add');
	};

	public getIntroText = () => {
		const { isDone } = this.props;
		if (isDone) {
			return i18n('react.impulsCoach.components.cardDetail.slogan.introText.done');
		}

		return i18n('react.impulsCoach.components.cardDetail.slogan.introText.undone');
	};

	public renderButtonBar = () => {
		const { isDone, isCheckable, isSequenceLoading, uiState } = this.props;
		let cardButtons: ButtonProps | null = null;

		if (!isCheckable) {
			return null;
		}

		if (uiState !== 'detail') {
			return null;
		}

		if (this.state.isEditing) {
			if (isDone) {
				cardButtons = {
					buttons: [
						{
							type: this.state.slogan ? '' : 'disabled',
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'completed',
							},
							labelBottom: i18n('react.impulsCoach.components.cardDetail.slogan.buttons.label.update'),
						},
						{
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'close',
							},
							labelBottom: i18n('react.impulsCoach.components.cardDetail.slogan.buttons.label.cancel'),
						},
					],
				} as ButtonProps;
			} else {
				cardButtons = {
					buttons: [
						{
							type: this.state.slogan ? '' : 'disabled',
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'completed',
							},
							labelBottom: i18n('react.impulsCoach.components.cardDetail.slogan.buttons.label.save'),
						},
						{
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'close',
							},
							labelBottom: i18n('react.impulsCoach.components.cardDetail.slogan.buttons.label.cancel'),
						},
					],
				} as ButtonProps;
			}
		} else {
			if (!isDone) {
				cardButtons = {
					buttons: [
						{
							type: this.state.slogan ? '' : 'disabled',
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'completed',
							},
							labelBottom: i18n('react.impulsCoach.components.cardDetail.slogan.buttons.label.save'),
						},
					],
				} as ButtonProps;
			}
		}

		if (cardButtons === null && this.isIOS) {
			return <div style={{ height: 114 }} />;
		}

		if (cardButtons === null) {
			return null;
		}

		return (
			<div className="m-card__button-bar">
				<ButtonBar
					loading={this.props.isUpdatingSlogan || this.props.isUiCompleting}
					disabled={isSequenceLoading}
					handleButtonClicks={this.handleButtonClicks}
					buttons={(cardButtons as any).buttons}
				/>
			</div>
		);
	};

	public render() {
		const { infoText, handleCardContentClick, handleShowDetailsClick } = this.props;

		return (
			<div>
				<div className="m-card__content" onClick={handleCardContentClick as any}>
					<div className="m-card__info">
						<h4 className="m-card__info-title">
							{i18n('react.impulsCoach.components.cardDetail.slogan.title')}
						</h4>
						{infoText && (
							<div className="m-card__info-line">
								<p className="m-card__info-text">{infoText}</p>
							</div>
						)}
					</div>
					<div className="m-card__link js-m-card__link">
						<Link
							onClick={handleShowDetailsClick as any}
							title={this.getButtonText()}
							modifier="button"
							openInNewWindow={false}
							iconRight={null}
						/>
					</div>
					<div className="m-card__detail">
						<Rich contents={this.getIntroText()} />
						<EditableArea
							text={this.state.optimisticSaveSlogan}
							placeholder={i18n('react.impulsCoach.components.card.slogan.placeholder')}
							isEditing={this.state.isEditing}
							onChange={this.handleSloganChange}
							onBlur={iOSScrollKeyboardPositionFix}
							onChangeEditStatus={this.handleEditStatusChange}
							onCloseFactory={this.overrideHandleCloseCard}
							onFocus={this.props.handleCardSloganFocus}
						/>
					</div>
					{this.renderButtonBar()}
				</div>
			</div>
		);
	}
}

export default withSequenceState(CardDetailSlogan, [Sequences.CARD_IS_CHECKING]);
